/**
 * The spec reducer — the headless heart of the builder.
 *
 * Every edit a user can make is an action here, and every action runs through
 * the SAME guard functions the compiler uses. That has two consequences worth
 * stating:
 *
 *  1. An illegal spec cannot be *constructed*, not merely rejected on submit.
 *     Landing on an incompatible shape resets the visualization to `table`;
 *     emptying the column shelf drops a "% of row" that no longer means
 *     anything.
 *  2. The UI never re-derives legality. It renders `dimensionAvailability()`
 *     and `vizAvailability()` — so a greyed-out control and a 400 from the
 *     server can never disagree.
 *
 * No DOM here. A host that wants its own builder UI consumes this hook and
 * draws whatever it likes.
 */
import {
  allGroupBy,
  allowsKind,
  canGroup,
  canShowAs,
  canVisualize,
  columnAvailability,
  type DateRangeValue,
  defaultSortDir,
  emptySpec,
  type FilterJoin,
  type FilterNode,
  type FilterRule,
  filterValueType,
  findDimension,
  findMeasure,
  groupLevel,
  isAdditive,
  isFilterGroup,
  isLevelSort,
  isRunnable,
  MAX_GROUP_BY,
  modelKinds,
  modelPeriods,
  newFilterId,
  periodProblem,
  periodRefusal,
  planQuery,
  type QueryPlan,
  type ReportKind,
  type ReportModel,
  type ReportSpec,
  reportPeriodPreset,
  resolveDateRange,
  type ShowAsMode,
  type SortSpec,
  type UIReportSpec,
  uiReportSpecSchema,
  type ValueFormat,
  type ValueFormatKey,
  type VizKind,
  valueFormatFor,
  withResolvedPeriod,
} from '@clockwise/semantic-core';
import { useCallback, useMemo, useReducer } from 'react';
import { applyFilterAction, type FilterAction } from '../filters/filter-actions';

/** Which shelf a dimension sits on. */
export type Axis = 'rows' | 'cols';

export type SpecAction =
  | { type: 'setKind'; kind: ReportKind }
  /** DETAIL: add a field to the column list, at `index` or at the end. */
  | { type: 'addColumn'; fieldId: string; index?: number }
  | { type: 'removeColumn'; fieldId: string }
  | { type: 'setColumns'; columns: string[] }
  | { type: 'toggleMeasure'; measureId: string }
  /** Replace the measure list, in order — the order they appear as columns. */
  | { type: 'setMeasures'; measures: string[] }
  | { type: 'setShowAs'; measureId: string; mode: ShowAsMode }
  /**
   * DISPLAY: set or clear one of a measure's format attributes.
   *
   * ONE action for every attribute there will ever be — a merge-patch keyed by
   * the `ValueFormat` field. `value: undefined` DELETES that field, which is
   * what keeps "absence means inherit" true: storing the value a measure
   * already had would pin the report to today's default and stop it tracking
   * the model.
   */
  | {
      type: 'setValueFormat';
      measureId: string;
      key: ValueFormatKey;
      value: ValueFormat[ValueFormatKey];
    }
  /** DISPLAY: drop every format attribute on one measure. */
  | { type: 'resetValueFormat'; measureId: string }
  | { type: 'addDimension'; dimensionId: string; axis: Axis; index?: number }
  | { type: 'removeDimension'; dimensionId: string }
  | { type: 'moveDimension'; dimensionId: string; axis: Axis; index?: number }
  | { type: 'reorder'; axis: Axis; from: number; to: number }
  /** MEMBERS: set the values of a dimension's `is any of` rule. */
  | { type: 'setFilter'; dimensionId: string; values: string[] }
  /** Add, remove or change one chip — the filter bar's own actions, see `filter-actions.ts`. */
  | FilterAction
  | { type: 'toggleFilterPause'; filterId: string }
  /** Two or more top-level rules become one group under `join`. */
  | { type: 'groupFilters'; filterIds: string[]; join: FilterJoin }
  | { type: 'ungroupFilter'; filterId: string }
  | { type: 'setFilterJoin'; filterId: string; join: FilterJoin }
  /** Replace an axis's whole comparison chain. */
  | { type: 'setSort'; axis: Axis; sort: SortSpec[] }
  /**
   * PIVOT: order ONE level of an axis. `by: null` returns it to the order the
   * query returned it in; `dir` defaults to the sensible one for what it names.
   */
  | {
      type: 'setFieldSort';
      axis: Axis;
      dimensionId: string;
      by: string | null;
      dir?: 'asc' | 'desc';
      /**
       * Column tuple keys to rank by. Given, it replaces whatever pinned this
       * level before; omitted, a pin survives as long as `by` does not change.
       * That is the difference between clicking a column header — which says
       * which column it means — and choosing from the field's own select,
       * which does not and must not silently unpin.
       */
      at?: readonly string[];
    }
  | { type: 'toggleSuppressRepeats'; dimensionId: string }
  | { type: 'toggleSubtotal'; dimensionId: string }
  | FoldAction
  | { type: 'toggleTotals' }
  | { type: 'setViz'; viz: VizKind }
  /**
   * The reporting period, as a SELECTION — a macro, or two explicit dates.
   *
   * The macro is what gets stored (see `ReportSpec.period`); the reducer
   * resolves it to the window the query runs over, so a spec never holds a
   * preset and a window that disagree.
   */
  | { type: 'setPeriod'; range: DateRangeValue }
  /** DETAIL: turn to the page starting at `offset` rows in. */
  | { type: 'setPage'; offset: number }
  /** DETAIL: how many rows a page holds. Always returns to the first page. */
  | { type: 'setPageSize'; size: number }
  | { type: 'reset'; spec: UIReportSpec };

interface ReducerContext {
  model: ReportModel;
}

/** Drop show-as modes that are no longer legal for the current shape. */
function pruneShowAs(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  const showAs: Record<string, ShowAsMode> = {};
  for (const [measureId, mode] of Object.entries(spec.showAs ?? {})) {
    const m = findMeasure(model, measureId);
    if (m && canShowAs(m, mode, spec).ok) showAs[measureId] = mode;
  }
  return { ...spec, showAs };
}

/**
 * Drop a sort whose measure is no longer selected.
 *
 * Sorting by a measure and then deselecting it leaves an axis ordered by
 * a column that is not in the result. The projection degrades quietly — every
 * tuple aggregates to null and the axis keeps its natural order — but the
 * compiler would emit an `ORDER BY` against a select list that no
 * longer has it, which is not a legal query.
 */
function pruneSort(spec: UIReportSpec): UIReportSpec {
  // `at` pins the ranking to one column TUPLE, so it only means anything while
  // the column axis still has the shape it was captured from. Left behind, it
  // matches no row: the entry orders nothing while the shelf goes on showing
  // it as active, which is worse than it simply having been dropped.
  const keep = (chain: SortSpec[]) =>
    chain
      .filter((sort) => isLevelSort(sort.by) || spec.measures.includes(sort.by))
      .map((sort) => {
        if (sort.at === undefined || sort.at.length === spec.cols.length) return sort;
        const { at: _stale, ...rest } = sort;
        return rest;
      });
  return { ...spec, rowSort: keep(spec.rowSort), colSort: keep(spec.colSort) };
}

/**
 * Put a pivot's PER-LEVEL entries in level order, and drop the ones that name
 * a level this axis does not have.
 *
 * Two things happen, and neither of them touches a whole-axis chain:
 *
 *  - an entry whose `on` is not on that axis is dropped. It orders nothing,
 *    and `validateSpec` refuses it outright;
 *  - the entries that remain are put in LEVEL order, so reordering the shelf
 *    reorders the chain with it.
 *
 * A LEGACY CHAIN IS CARRIED THROUGH UNTOUCHED, at the front, in the order it
 * arrived in. An earlier cut read it onto the levels instead — its head to
 * each of them — on the reasoning that `orderTuples` applies the whole chain
 * at every level anyway. That is true of `orderTuples` and false of the report:
 * a whole-axis chain over an unpivoted axis is `plan`'s SQL fast path, where
 * the flat result is ordered by the LEAF value, and expanding it into per-level
 * entries moves it into memory to be ranked by each level's GROUP TOTAL. Two
 * different orders, and a saved report changed which one it showed just by
 * being opened. Worse where the measure is non-additive: the expansion's outer
 * entry is then refused outright and that level loses its order altogether.
 *
 * The cost is that a field shows "Data source order" while a legacy chain is
 * what actually orders it — the field has no order OF ITS OWN, which is the truth,
 * and under-reporting is the safe side of this trade. Choosing one writes an
 * `on` entry that takes precedence at that level and leaves the rest reading
 * the chain, which is exactly what `orderTuples` already does.
 *
 * At the front, and not merely somewhere, because position decides placement:
 * `plan` gives `ORDER BY` to the first entry it can, so a legacy chain keeps
 * the SQL path it had and the per-level entries added beside it go to memory.
 */
function levelSort(chain: readonly SortSpec[], dims: readonly string[]): SortSpec[] {
  const out: SortSpec[] = chain.filter((s) => s.on === undefined);
  for (const dim of dims) {
    const own = chain.find((s) => s.on === dim);
    if (own) out.push(own);
  }
  return out;
}

/**
 * Drop a per-level order the PLAN will refuse to apply.
 *
 * Ranking a level by a measure ranks it by that group's total, so a
 * non-additive measure is refused wherever a total has to be taken — the same
 * `aggregatesToRank` test `planQuery` applies, restated here because the
 * refusal has to reach the SPEC and not just the picker.
 *
 * Greying the option out only covers choosing it. An entry chosen while it was
 * legal — an average on the innermost level, with no column axis — stops being
 * legal the moment a second row dimension or any column dimension is added,
 * and the plan then drops it silently. Left in the spec, the field's select
 * went on showing that measure and the pivot header went on painting its
 * arrow, for an order the report did not have.
 *
 * Row entries only. `planQuery`'s column loop has never applied this test, and
 * a prune that refused more than the plan does would be the same disagreement
 * pointing the other way. Per-level entries only, for the same reason: a
 * whole-axis chain is judged by `pivoted` alone, and tightening that would
 * change what a saved report means.
 */
function pruneUnrankable(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  const pivoted = spec.cols.length > 0;
  return {
    ...spec,
    rowSort: spec.rowSort.filter((sort) => {
      if (sort.on === undefined) return true;
      const measure = findMeasure(model, sort.by);
      if (!measure || isAdditive(measure)) return true;
      return !pivoted && spec.rows.indexOf(sort.on) === spec.rows.length - 1;
    }),
  };
}

/**
 * Drop a display flag whose dimension is not a row dimension.
 *
 * `withoutDimension` covers removal, but a spec can arrive already wrong — a
 * host's `initialSpec`, a saved report, a `reset`. `validateSpec` refuses those
 * ids, and no control in the builder can clear one, so without this the draft
 * is permanently unrunnable with nothing on screen to fix.
 */
function pruneDisplayFlags(spec: UIReportSpec): UIReportSpec {
  const onRows = (id: string) => spec.rows.includes(id);
  return {
    ...spec,
    subtotals: spec.subtotals.filter(onRows),
    suppressRepeats: spec.suppressRepeats.filter(onRows),
    // Written only where the spec already carries them, so a report that never
    // folded anything does not start saving two empty lists.
    ...(spec.collapsed && { collapsed: spec.collapsed.filter(onRows) }),
    ...(spec.toggledGroups && {
      toggledGroups: spec.toggledGroups.filter((key) => groupLevel(key) < spec.rows.length - 1),
    }),
  };
}

/** Fall back to a table when the shape no longer supports the chosen chart. */
function settleViz(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  return canVisualize(model, spec, spec.viz).ok ? spec : { ...spec, viz: 'table' };
}

/**
 * A listing's own cross-field rules.
 *
 * Only three, because a listing has almost no cross-field state: a column must
 * still be listable, a sort must name a column the listing selects, and the
 * result is a table. The pivot-only fields are left exactly as they are — they
 * are ignored rather than refused (see `validateSpec`), and a draft that
 * quietly rewrote them would make switching kind lossy in a way nothing on
 * screen explains.
 */
function settleDetail(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  const available = columnAvailability(model);
  const columns = spec.columns.filter((id) => available.get(id)?.ok === true);
  return {
    ...spec,
    columns,
    // `on` names a nesting level and a listing has none — one arrives only by
    // switching kind on a draft, or from a host handing in a pivot's spec. Left
    // on, `validateSpec` refuses the listing for a field no control can clear.
    rowSort: spec.rowSort
      .filter((sort) => columns.includes(sort.by))
      .map(({ on: _level, ...rest }) => rest),
    viz: 'table',
  };
}

/**
 * Drop ids this model no longer has at all.
 *
 * A saved report outlives the vocabulary it was written in. A dimension or a
 * measure can be RETIRED — `accountController`, `Pools Over Budget` and
 * `% Pools Over Budget` left Budget vs Actuals in September 2026 — and
 * `validateSpec` answers every spec that still names one with "Unknown
 * dimension …", on every load, for good: no control in the builder can clear an
 * id whose dimension is not in the catalogue, and nothing migrates the stored
 * specs. A report somebody saved becomes a 400 with no way back except editing
 * `report_definitions.spec` by hand.
 *
 * So they are dropped here, the way `settleDetail` already drops a column that
 * stopped being listable, and for the same reason. The other prunes then
 * cascade off these lists: `pruneSort` drops a sort naming a measure that is no
 * longer selected, `pruneDisplayFlags` drops a subtotal on a row that is gone.
 *
 * UNKNOWN, not merely unusable — a dimension the model still has but will not
 * group by keeps its refusal, because that one is a rule somebody can read and
 * a spec that could become legal again. An id with nothing behind it cannot.
 *
 * Silent, like the kind coercion below: the report opens without the retired
 * group rather than not opening at all. It is also the ONE case the builder
 * cannot narrate, since there is no label left to name the thing that went.
 */
function pruneUnknown(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  const knownDimension = (id: string) => findDimension(model, id) !== undefined;
  return {
    ...spec,
    // Checked for BOTH kinds by `validateSpec`, before it branches — so a
    // listing carrying a retired filter is refused exactly like a pivot.
    // A group loses only the child whose dimension went, and collapses to a
    // plain rule if that leaves one — a connector with one side is not a group.
    filters: spec.filters.flatMap((node): FilterNode[] => {
      if (!isFilterGroup(node)) return knownDimension(node.field) ? [node] : [];
      const children = node.children.filter((child) => knownDimension(child.field));
      if (children.length > 1) return [{ ...node, children }];
      return children.length === 1 ? [children[0] as FilterRule] : [];
    }),
    rows: spec.rows.filter(knownDimension),
    cols: spec.cols.filter(knownDimension),
    measures: spec.measures.filter((id) => findMeasure(model, id) !== undefined),
    // `columns` is NOT touched: `settleDetail` already filters it through
    // `columnAvailability`, which knows the difference between an id that is
    // gone and one that is merely not listable. Two rules over one field is how
    // they drift apart.
  };
}

/**
 * Move a period this model does not offer onto the first one it does.
 *
 * A spec can arrive asked over a period the model refuses — a report saved over
 * a custom range before Budget vs Actuals stopped offering one, a drill's
 * hand-off, a host's `initialSpec`. Left alone it runs over that window with a
 * chip that names it and a panel that cannot reach it. Moved, it opens over a
 * period the reader can see and change. The builder says that this happened
 * (`savedOverPeriod`); saving keeps the new period.
 *
 * A spec with no `period` at all predates the field and reads as `custom`.
 */
function settlePeriod(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  if (!periodRefusal(model, spec.period ?? 'custom')) return spec;
  const period = modelPeriods(model)[0];
  const window = period && resolveDateRange({ preset: period });
  return period && window ? { ...spec, period, from: window.from, to: window.to } : spec;
}

/** Re-run every cross-field rule. Cheap, and it means no action can forget one. */
function settle(model: ReportModel, spec: UIReportSpec): UIReportSpec {
  // The kind FIRST, because every rule under it is per-kind. A spec can arrive
  // naming one this model does not offer — a saved report that predates the
  // model's `kinds`, a host's `initialSpec` — and `validateSpec` refuses it, so
  // left alone it is a draft the reader cannot run and has no control to fix
  // (the builder draws no button for a kind the model refuses). Coerced, it
  // opens on the kind the model does offer with its shelves empty, which is a
  // page somebody can carry on from. The builder says that this happened; a
  // coercion nobody is told about is a report that quietly opens blank.
  //
  // BOTH PAGING FIELDS GO, exactly as `setKind` drops them: a listing's `limit`
  // is a page size and a pivot's is a cap on GROUPS, so a saved page size of
  // 100 carried across would cap the pivot at a hundred groups and report
  // itself truncated — and the pager that could raise it is a listing's
  // control, so there would be nothing on screen to see or undo it with.
  // `offset` is the same field one over, and it would be saved back.
  let settled = settlePeriod(model, pruneUnknown(model, spec));
  if (!allowsKind(model, settled.kind)) {
    const { limit: _cap, offset: _page, ...rest } = settled;
    settled = { ...rest, kind: modelKinds(model)[0] };
  }
  if (settled.kind === 'detail') return settleDetail(model, settled);
  const pruned = pruneUnrankable(model, pruneDisplayFlags(pruneSort(pruneShowAs(model, settled))));
  return settleViz(model, {
    ...pruned,
    rowSort: levelSort(pruned.rowSort, pruned.rows),
    colSort: levelSort(pruned.colSort, pruned.cols),
  });
}

function withoutDimension(spec: UIReportSpec, dimensionId: string): UIReportSpec {
  return {
    ...spec,
    rows: spec.rows.filter((d) => d !== dimensionId),
    cols: spec.cols.filter((d) => d !== dimensionId),
    // A display flag for a dimension that is gone is a flag nothing can turn
    // off, because its control went with it.
    suppressRepeats: spec.suppressRepeats.filter((d) => d !== dimensionId),
    subtotals: spec.subtotals.filter((d) => d !== dimensionId),
    ...(spec.collapsed && { collapsed: spec.collapsed.filter((d) => d !== dimensionId) }),
  };
}

function insertAt(list: string[], id: string, index?: number): string[] {
  const next = [...list];
  next.splice(index === undefined || index > next.length ? next.length : index, 0, id);
  return next;
}

/**
 * Everything a listing's PAGE is taken against.
 *
 * `offset` means "row 500 of THIS query", so it survives only as long as the
 * query does: editing a filter while on page 7 and staying on page 7 shows the
 * seventh page of an answer the reader has never seen the first page of, and
 * the report looks empty for no reason they can see.
 *
 * Compared rather than enumerated deliberately. A list of page-resetting
 * actions is a list a future action forgets to join, and the failure is silent;
 * this way anything that moves the query resets the page by construction, and
 * anything that does not — a total, a display flag — leaves the reader where
 * they were.
 */
function pageContext(spec: UIReportSpec): string {
  return JSON.stringify([
    spec.model,
    spec.kind,
    spec.from,
    spec.to,
    spec.columns,
    spec.filters,
    spec.rowSort,
    spec.limit,
  ]);
}

export type FoldAction =
  /** PIVOT: fold or unfold ONE group, by its `groupKey()`. */
  | { type: 'toggleGroup'; key: string }
  /**
   * PIVOT: fold or unfold EVERY group at one row level — the level's default,
   * with the reader's per-group flips at that level cleared.
   */
  | { type: 'setLevelCollapsed'; dimensionId: string; collapsed: boolean };

type Foldable = Pick<ReportSpec, 'rows' | 'collapsed' | 'toggledGroups'>;

/**
 * Apply one fold action to whatever carries a row axis and its folds.
 *
 * Exported because the builder is not the only reader who folds: a placed
 * dashboard tile folds too, into state of its own that is never saved. One
 * function, so the two cannot disagree about which levels may fold.
 */
export function applyFold<S extends Foldable>(spec: S, action: FoldAction): S {
  if (action.type === 'toggleGroup') {
    // The reducer is the gate, not the control: a flip on a level that
    // cannot fold — the innermost, or one the axis does not have — is a flip
    // nothing draws and nothing could ever clear.
    const level = groupLevel(action.key);
    if (!(level >= 0 && level < spec.rows.length - 1)) return spec;
    const flipped = spec.toggledGroups ?? [];
    return {
      ...spec,
      toggledGroups: flipped.includes(action.key)
        ? flipped.filter((key) => key !== action.key)
        : [...flipped, action.key],
    };
  }
  const level = spec.rows.indexOf(action.dimensionId);
  if (level === -1 || level >= spec.rows.length - 1) return spec;
  const others = (spec.collapsed ?? []).filter((id) => id !== action.dimensionId);
  return {
    ...spec,
    collapsed: action.collapsed ? [...others, action.dimensionId] : others,
    // "Fold every customer" means every one, including the ones unfolded
    // by hand a moment ago.
    toggledGroups: (spec.toggledGroups ?? []).filter((key) => groupLevel(key) !== level),
  };
}

export function specReducer(
  ctx: ReducerContext,
  spec: UIReportSpec,
  action: SpecAction,
): UIReportSpec {
  let next = reduceSpec(ctx, spec, action);
  // A folded group is keyed by the PATH to it, and the path is the row axis:
  // re-nest or reorder the axis and "0:acme" names a group at a different
  // level, or none. Compared rather than enumerated, like the page below, so
  // no action that moves the rows can forget to clear them.
  if (next.toggledGroups?.length && next.rows.join(' ') !== spec.rows.join(' ')) {
    const { toggledGroups: _stale, ...rest } = next;
    next = rest;
  }
  // Turning the page is the one action allowed to move the page.
  if (action.type === 'setPage' || !next.offset) return next;
  if (pageContext(next) === pageContext(spec)) return next;
  const { offset: _page, ...atFirstPage } = next;
  return atFirstPage;
}

function reduceSpec(ctx: ReducerContext, spec: UIReportSpec, action: SpecAction): UIReportSpec {
  const model = ctx.model;

  switch (action.type) {
    case 'setKind': {
      if (action.kind === spec.kind) return spec;
      // The reducer is the gate, not the control — same rule as `addColumn`.
      // A model may offer only one kind, and the button for the other one is
      // never drawn; this is what makes that a property of the spec rather
      // than of the markup.
      if (!allowsKind(model, action.kind)) return spec;
      // KEPT: the model, the period, the filters — they mean the same thing in
      // a listing as in a summary, which is why switching is cheap enough to
      // try. DROPPED: everything that names an axis or a column, because a
      // measure is not a column and a group-by is not a field. Deliberately
      // gentler than switching MODEL, which remounts; the two controls sit next
      // to each other, so the difference has to be intentional.
      //
      // BOTH paging fields go with them. A pivot's cap is a cap on GROUPS and
      // a listing's is a cap on rows, so carrying one over silently reshapes
      // the other — and neither kind offers a control to see or undo it, so an
      // inherited `limit: 50` would be a fifty-row listing reporting itself
      // truncated with nothing on screen to explain why. `offset` is the same
      // field one over: kept, a saved listing on page 2 would reopen on page 2
      // after a round trip through Pivot, missing its first five hundred rows.
      const {
        limit: _cap,
        offset: _page,
        collapsed: _folded,
        toggledGroups: _flips,
        ...rest
      } = spec;
      return settle(model, {
        ...rest,
        kind: action.kind,
        measures: [],
        rows: [],
        cols: [],
        columns: [],
        rowSort: [],
        colSort: [],
        showAs: {},
        valueFormat: {},
        subtotals: [],
        suppressRepeats: [],
        totals: { row: false, col: false },
        viz: 'table',
      });
    }

    case 'addColumn': {
      // The reducer is the gate, not the picker — same rule as `addDimension`.
      // Unlike a group-by there is no ceiling and no `groupable` check: a
      // correlation id is a bad axis and a perfectly good column.
      if (spec.columns.includes(action.fieldId)) return spec;
      if (columnAvailability(model).get(action.fieldId)?.ok !== true) return spec;
      return settle(model, {
        ...spec,
        columns: insertAt(spec.columns, action.fieldId, action.index),
      });
    }

    case 'removeColumn':
      return settle(model, {
        ...spec,
        columns: spec.columns.filter((id) => id !== action.fieldId),
      });

    case 'setColumns':
      return settle(model, { ...spec, columns: action.columns });

    case 'toggleMeasure': {
      const has = spec.measures.includes(action.measureId);
      const measures = has
        ? spec.measures.filter((m) => m !== action.measureId)
        : [...spec.measures, action.measureId];
      return settle(model, { ...spec, measures });
    }

    case 'setMeasures':
      return settle(model, { ...spec, measures: action.measures });

    case 'setShowAs': {
      const measure = findMeasure(model, action.measureId);
      if (!measure || !canShowAs(measure, action.mode, spec).ok) return spec;
      return settle(model, {
        ...spec,
        showAs: { ...spec.showAs, [action.measureId]: action.mode },
      });
    }

    case 'setValueFormat': {
      const next: ValueFormat = { ...valueFormatFor(spec, action.measureId) };
      if (action.value === undefined) delete next[action.key];
      else Object.assign(next, { [action.key]: action.value });

      const valueFormat = { ...spec.valueFormat };
      // An EMPTY object is no entry at all, so a measure returned to its
      // defaults leaves nothing behind in the saved spec and "has this report
      // changed?" stays an honest question.
      if (Object.keys(next).length === 0) delete valueFormat[action.measureId];
      else valueFormat[action.measureId] = next;

      // No `settle` — formatting cannot make a spec illegal. It changes no
      // measure, no axis and no filter, so there is nothing for the guards to
      // reconsider.
      return { ...spec, valueFormat };
    }

    case 'resetValueFormat': {
      if (!spec.valueFormat?.[action.measureId]) return spec;
      const valueFormat = { ...spec.valueFormat };
      delete valueFormat[action.measureId];
      return { ...spec, valueFormat };
    }

    case 'addDimension': {
      // The reducer is the gate, not the picker. A host driving this hook
      // directly — the documented headless path — must not be able to build a
      // spec the compiler will refuse, and `dimensionAvailability` already
      // computes exactly this verdict for the UI to grey out.
      const dim = findDimension(model, action.dimensionId);
      if (!dim || !canGroup(dim)) return spec;
      if (allGroupBy(spec).includes(action.dimensionId)) return spec;
      // The ceiling counts BOTH axes — it is a limit on nesting depth, not on
      // either shelf.
      if (allGroupBy(spec).length >= MAX_GROUP_BY) return spec;
      const next = {
        ...spec,
        [action.axis]: insertAt(spec[action.axis], action.dimensionId, action.index),
      } as UIReportSpec;
      return settle(model, next);
    }

    case 'removeDimension':
      return settle(model, withoutDimension(spec, action.dimensionId));

    case 'moveDimension': {
      // Move is remove-then-insert so dragging within a shelf and between
      // shelves are the same operation.
      const from = spec.rows.includes(action.dimensionId) ? 'rows' : 'cols';
      const originalIndex = spec[from].indexOf(action.dimensionId);
      if (originalIndex === -1) return spec;

      const stripped = withoutDimension(spec, action.dimensionId);
      // Dropping later in the SAME shelf: the index the user saw was computed
      // before removal, so shift it back by one.
      const adjusted =
        from === action.axis && action.index !== undefined && action.index > originalIndex
          ? action.index - 1
          : action.index;
      // `withoutDimension` drops the row-only display flags, which is right
      // when the dimension is LEAVING the row axis and wrong when it is only
      // changing position on it — otherwise a drag silently undoes the toggles
      // that Alt+↑/↓ keeps, and the same gesture has two outcomes.
      const flags =
        action.axis === 'rows'
          ? {
              suppressRepeats: spec.suppressRepeats,
              subtotals: spec.subtotals,
              ...(spec.collapsed && { collapsed: spec.collapsed }),
            }
          : {};
      return settle(model, {
        ...stripped,
        ...flags,
        [action.axis]: insertAt(stripped[action.axis], action.dimensionId, adjusted),
      } as UIReportSpec);
    }

    case 'reorder': {
      const list = [...spec[action.axis]];
      const moved = list.splice(action.from, 1)[0];
      if (moved === undefined) return spec;
      list.splice(action.to, 0, moved);
      // SETTLED, because the shelf's order is now also the sort chain's: a
      // level that moves takes its order with it, and `levelSort` is what puts
      // the chain back in step. Unsettled, dragging a row up left the entries
      // in their old sequence — which changes nothing about the RESULT, since
      // every entry names its own level, and everything about reading the
      // chain back out of the spec.
      return settle(model, { ...spec, [action.axis]: list } as UIReportSpec);
    }

    /**
     * The members case, kept as its own action.
     *
     * Every filter was this before operators existed and most still are, so the
     * bar's picker writes values rather than assembling a rule — the action
     * says what the reader did. A dimension already carrying a rule with a
     * different operator is left alone: replacing it would answer a question
     * nobody asked.
     */
    case 'setFilter': {
      const at = spec.filters.findIndex(
        (node) => !isFilterGroup(node) && node.field === action.dimensionId && node.op === 'in',
      );
      if (action.values.length === 0) {
        return at < 0 ? spec : { ...spec, filters: spec.filters.filter((_, i) => i !== at) };
      }
      const value = { kind: 'list', values: action.values } as const;
      if (at < 0) {
        // The dimension id is the NATURAL id — it is what the legacy
        // conversion writes, and what keeps a re-read stable — but only while
        // it is free. A rule already carrying it (the same column filtered
        // with another operator, whose id is also its field) would otherwise
        // be joined by a second node with the same id, which `validateFilters`
        // refuses for the whole spec.
        const taken = new Set(spec.filters.map((node) => node.id));
        const rule: FilterRule = {
          id: taken.has(action.dimensionId) ? newFilterId(action.dimensionId) : action.dimensionId,
          field: action.dimensionId,
          op: 'in',
          value,
          paused: false,
        };
        return { ...spec, filters: [...spec.filters, rule] };
      }
      return {
        ...spec,
        filters: spec.filters.map((node, i) =>
          i === at ? { ...(node as FilterRule), value } : node,
        ),
      };
    }

    /**
     * The filter bar's own actions: add a chip, remove one, change one.
     *
     * Their meaning lives in `applyFilterAction`, shared with every other
     * host of the bar — a dashboard applies the same three to its URL — so a
     * filter edited here and one edited there cannot come out different.
     */
    case 'addFilter':
    case 'removeFilter':
    case 'setFilterRule': {
      const filters = applyFilterAction(spec.filters, action, (field) =>
        filterValueType(model, field),
      );
      return filters === spec.filters ? spec : { ...spec, filters: [...filters] };
    }

    /** On the bar, struck through, out of the query. */
    case 'toggleFilterPause':
      return {
        ...spec,
        filters: spec.filters.map((node) =>
          node.id === action.filterId && !isFilterGroup(node)
            ? { ...node, paused: !node.paused }
            : node,
        ),
      };

    /**
     * Two chips become one bracketed chip.
     *
     * The group takes the position of the FIRST of them, because that is where
     * the reader's eye already is; the other leaves its place. Only top-level
     * rules can be grouped — one level is the whole design.
     */
    case 'groupFilters': {
      const ids = new Set(action.filterIds);
      const children = spec.filters.filter(
        (node): node is FilterRule => ids.has(node.id) && !isFilterGroup(node),
      );
      if (children.length < 2) return spec;
      const at = spec.filters.findIndex((node) => ids.has(node.id));
      const group = { id: newFilterId('g'), join: action.join, children };
      const rest = spec.filters.filter((node) => !ids.has(node.id));
      return { ...spec, filters: [...rest.slice(0, at), group, ...rest.slice(at)] };
    }

    /** The brackets come off; the rules stay, in the order they were in. */
    case 'ungroupFilter': {
      const at = spec.filters.findIndex((node) => node.id === action.filterId);
      const group = at < 0 ? undefined : spec.filters[at];
      if (!group || !isFilterGroup(group)) return spec;
      return {
        ...spec,
        filters: [...spec.filters.slice(0, at), ...group.children, ...spec.filters.slice(at + 1)],
      };
    }

    case 'setFilterJoin':
      return {
        ...spec,
        filters: spec.filters.map((node) =>
          node.id === action.filterId && isFilterGroup(node)
            ? { ...node, join: action.join }
            : node,
        ),
      };

    case 'setSort':
      // SETTLED, like every other write. The tabular Sort shelf is the only
      // caller left, and its chain still has to lose an entry naming a column
      // that has just been removed — but a pivot's chain reaching here would
      // also need putting back into level order, and a write that skipped that
      // would leave the shelves and the chain disagreeing about which level is
      // which.
      return settle(
        model,
        action.axis === 'rows'
          ? { ...spec, rowSort: action.sort }
          : { ...spec, colSort: action.sort },
      );

    case 'setFieldSort': {
      const dims = action.axis === 'rows' ? spec.rows : spec.cols;
      // The reducer is the gate, not the control. A level that is not on this
      // axis has no order to set, and writing one anyway builds a spec
      // `validateSpec` refuses for a field the builder draws no control for.
      if (!dims.includes(action.dimensionId)) return spec;
      if (action.by !== null && !isLevelSort(action.by) && !spec.measures.includes(action.by)) {
        return spec;
      }
      const chain = action.axis === 'rows' ? spec.rowSort : spec.colSort;
      const previous = chain.find((s) => s.on === action.dimensionId);
      const rest = chain.filter((s) => s.on !== action.dimensionId);
      if (action.by === null) {
        return settle(model, {
          ...spec,
          [action.axis === 'rows' ? 'rowSort' : 'colSort']: rest,
        } as UIReportSpec);
      }
      const entry: SortSpec = {
        on: action.dimensionId,
        by: action.by,
        dir: action.dir ?? defaultSortDir(model, action.by),
        // `at` pins the ranking to one column, and it was set by clicking THAT
        // column's header. Stated by the caller it wins; otherwise it survives
        // a direction flip and dies with a change of what is ranked. "March's
        // hours, descending" is still about March when it becomes ascending,
        // and is not about March at all once it becomes the customer's name.
        ...(action.at?.length
          ? { at: [...action.at] }
          : action.at === undefined && previous?.by === action.by && previous.at?.length
            ? { at: [...previous.at] }
            : {}),
      };
      // `settle` puts it in level order, so the chain is rebuilt rather than
      // spliced — where the entry lands is a property of the shelf.
      return settle(model, {
        ...spec,
        [action.axis === 'rows' ? 'rowSort' : 'colSort']: [...rest, entry],
      } as UIReportSpec);
    }

    case 'toggleSubtotal': {
      const on = spec.subtotals.includes(action.dimensionId);
      return {
        ...spec,
        subtotals: on
          ? spec.subtotals.filter((id) => id !== action.dimensionId)
          : [...spec.subtotals, action.dimensionId],
      };
    }

    case 'toggleGroup':
    case 'setLevelCollapsed':
      return applyFold(spec, action);

    case 'toggleSuppressRepeats': {
      const on = spec.suppressRepeats.includes(action.dimensionId);
      return {
        ...spec,
        suppressRepeats: on
          ? spec.suppressRepeats.filter((id) => id !== action.dimensionId)
          : [...spec.suppressRepeats, action.dimensionId],
      };
    }

    case 'toggleTotals': {
      // One switch, both flags. The total row, the total column and the grand
      // total where they meet are one idea — "show me the totals" — and the
      // renderer gates the corner on BOTH, so setting one alone leaves the
      // grand total unreachable. A report with no column axis still shows only
      // the bottom row, because the renderer needs a column axis to total
      // across.
      const on = !(spec.totals.row && spec.totals.col);
      return { ...spec, totals: { row: on, col: on } };
    }

    case 'setViz':
      return canVisualize(model, spec, action.viz).ok ? { ...spec, viz: action.viz } : spec;

    case 'setPeriod': {
      // REFUSED rather than stored, for a selection that does not resolve (a
      // half-picked custom range) or one nothing downstream would run (a span
      // past `MAX_PERIOD_DAYS`). The spec drives a live query, so an
      // unrunnable period here is a preview that goes blank while somebody is
      // still choosing the second date. The control that sends this keeps its
      // own draft and says why it has not been taken — see `PeriodChip`.
      //
      // A period the model does not offer is refused the same way, by the same
      // sentence the chip shows (`periodRefusal`), so the chip keeps its panel
      // open with the reason instead of closing over a change nobody took.
      const resolved = resolveDateRange(action.range);
      if (!resolved || periodProblem(resolved.from, resolved.to)) return spec;
      if (periodRefusal(model, action.range.preset)) return spec;
      return {
        ...spec,
        period: reportPeriodPreset(action.range.preset),
        from: resolved.from,
        to: resolved.to,
      };
    }

    case 'setPage': {
      // The first page is the ABSENCE of an offset, not an offset of zero —
      // `planKey` reads the field, and a spec that saved `offset: 0` would look
      // like a different query from the identical one that never paged.
      const offset = Math.max(0, Math.trunc(action.offset));
      if (offset === 0) {
        const { offset: _first, ...rest } = spec;
        return rest;
      }
      return { ...spec, offset };
    }

    case 'setPageSize':
      if (!Number.isFinite(action.size) || action.size < 1) return spec;
      // The page reset is NOT done here. `limit` is part of `pageContext`, so
      // `specReducer` drops the offset for this action the same way it does for
      // a filter or a sort — one mechanism, in one place, rather than two that
      // can drift apart.
      return { ...spec, limit: Math.trunc(action.size) };

    case 'reset':
      // RESOLVED first: a spec arriving from outside carries a macro plus the
      // window it meant when it was written, and the window is the stale half.
      //
      // THROUGH `withSchemaDefaults`, exactly like the initial spec. This is
      // the path a saved report actually takes — the page mounts before its
      // query resolves, so the spec arrives as a reset rather than as
      // `initial` — and skipping the repair here meant the older-spec repair
      // ran on the one path old specs never use.
      return settle(model, withResolvedPeriod(withSchemaDefaults(action.spec)));

    default:
      return spec;
  }
}

export interface UseReportSpec {
  spec: UIReportSpec;
  dispatch: (action: SpecAction) => void;
  /** Non-null once the draft has a measure and a row dimension. */
  runnable: ReportSpec | null;
  /** How the runnable spec splits between the query and the projection. */
  plan: QueryPlan | null;
  /**
   * Sorting by a value means "biggest first"; by a name, A→Z. `on` names the
   * pivot LEVEL a click landed on, defaulting to the outermost.
   */
  setSortBy: (axis: Axis, by: string, at?: readonly string[], on?: string) => void;
}

/**
 * Materialise the schema's defaults on a spec that arrives from storage.
 *
 * A saved spec is JSONB written by whatever `reportSpecSchema` looked like on
 * the day it was saved, and it is handed back to the builder verbatim — nothing
 * on the read path parses it. So a report saved before a field existed arrives
 * MISSING that field, and `valueFormat: {}`'s promise that "every spec ever
 * saved parses unchanged" only holds for code that actually parses. The
 * reducer did not, and `spec.valueFormat[id]` threw on every report older than
 * the formatting feature — the whole builder replaced by an error boundary the
 * moment somebody picked a decimal place.
 *
 * Doing it here rather than guarding each read keeps the repair in ONE place
 * and covers the next defaulted field as well as this one. It sits beside
 * `settle` deliberately: both exist so that a spec from an older release opens
 * as a page somebody can carry on from.
 *
 * FIELD BY FIELD, NOT ONE `safeParse` OF THE WHOLE SPEC. A whole-spec parse
 * fails as a unit, so a single value this bundle cannot read would skip the
 * defaults for every OTHER field — and that is not a hypothetical: an older tab
 * outlives a deploy, and a report saved with a `viz` or `kind` added since is
 * exactly the spec most likely to also predate a defaulted field. `settle`
 * would then reach `spec.subtotals.filter(...)` and throw the same class of
 * error this function exists to prevent. Per key, one unreadable field costs
 * only itself.
 *
 * ONLY GAPS ARE FILLED — a key that already has a value keeps it, and a key
 * this bundle has never heard of is carried through untouched. Rebuilding the
 * spec from the schema instead would strip that key, and an older tab would
 * drop a newer field on the next save: the silent loss `valueFormat`'s own
 * schema gave up `.strict()` to avoid. `model`, `from` and `to` have no default
 * and so are never invented here.
 */
function withSchemaDefaults(spec: UIReportSpec): UIReportSpec {
  const filled: Record<string, unknown> = { ...spec };
  for (const [key, field] of Object.entries(uiReportSpecSchema.shape)) {
    if (filled[key] !== undefined) continue;
    // A field wrapped in `.default()` answers `undefined` with that default;
    // a required one fails, and is left for `validateSpec` to report.
    const fallback = field.safeParse(undefined);
    if (fallback.success) filled[key] = fallback.data;
  }
  return filled as UIReportSpec;
}

export function useReportSpec(model: ReportModel, initial?: UIReportSpec): UseReportSpec {
  const [spec, rawDispatch] = useReducer(
    (s: UIReportSpec, a: SpecAction) => specReducer({ model }, s, a),
    // A host-supplied spec is settled before it is ever shown: a saved report
    // may predate a rule, and an illegal draft the user cannot see is one they
    // cannot fix.
    // A FRESH session starts tabular where the model offers it. Listing the
    // rows is the commoner errand and the shape somebody usually arrives
    // already wanting; summarising is one click away. On a model that offers
    // only one kind there is no preference to express — it opens on that one.
    // Distinct from `reportSpecSchema`'s default, which is `'pivot'` so a spec
    // saved before `kind` existed still parses as what it was — that default
    // is about old data, this one is about a blank page.
    initial
      ? // A SAVED REPORT'S MACRO IS RE-ANSWERED HERE, and this is the only
        // place it happens: the dates that reach the screen are the dates that
        // get queried, exported and saved back. Resolving later — on the way
        // to the server — would run a window nobody was shown.
        settle(model, withResolvedPeriod(withSchemaDefaults(initial)))
      : emptySpec(
          model.id,
          // A macro rather than a computed window, so the period a blank
          // builder opens on is a CHOICE the reader can see named on the bar
          // and keep, rather than two dates that happen to be this month's.
          { preset: 'current_month' },
          allowsKind(model, 'detail') ? 'detail' : modelKinds(model)[0],
        ),
  );

  const dispatch = useCallback((action: SpecAction) => rawDispatch(action), []);

  /**
   * What clicking a header, or a sort chip, should do next.
   *
   * A LISTING has one order over one flat result: naming the entry it already
   * leads with flips its direction, and naming a new one puts it at the FRONT
   * of the chain — the thing you just asked for is the thing you want to decide
   * the order, and whatever was there becomes its tie-breaker.
   *
   * A PIVOT'S axis is nested, so a click lands on a LEVEL. `on` says which; a
   * click that does not name one means the outermost, because that is what a
   * measure header is asking about — "rank these customers by March" is a
   * statement about the customers, not about the employees under one of them.
   * Naming what that level is already ordered by flips its direction, exactly
   * as the listing does.
   */
  const setSortBy = useCallback(
    (axis: Axis, by: string, at?: readonly string[], on?: string) => {
      const chain = axis === 'rows' ? spec.rowSort : spec.colSort;
      const same = (s: SortSpec | undefined) =>
        !!s && s.by === by && (s.at ?? []).join('\u0000') === (at ?? []).join('\u0000');

      if (spec.kind !== 'detail') {
        const level = on ?? (axis === 'rows' ? spec.rows : spec.cols)[0];
        if (level === undefined) return;
        const current = chain.find((s) => s.on === level);
        const dir = same(current)
          ? current?.dir === 'asc'
            ? 'desc'
            : 'asc'
          : defaultSortDir(model, by);
        // Through the action rather than around it, so a click and the field's
        // own select build the entry by one set of rules.
        dispatch({ type: 'setFieldSort', axis, dimensionId: level, by, dir, at });
        return;
      }

      const lead = chain[0];
      const entry: SortSpec = same(lead)
        ? { ...(lead as SortSpec), dir: lead?.dir === 'asc' ? 'desc' : 'asc' }
        : { by, dir: defaultSortDir(model, by), ...(at?.length ? { at: [...at] } : {}) };
      dispatch({ type: 'setSort', axis, sort: [entry, ...chain.filter((s) => !same(s))] });
    },
    [dispatch, model, spec.kind, spec.rows, spec.cols, spec.rowSort, spec.colSort],
  );

  const runnable = useMemo(() => (isRunnable(spec) ? spec : null), [spec]);
  const plan = useMemo(() => (runnable ? planQuery(model, runnable) : null), [model, runnable]);

  return { spec, dispatch, runnable, plan, setSortBy };
}
